import { Blog, BlogHome, ContentPageTheme, Media } from "@/services/bedrock";

export type BlogHomeDraft = {
  title: string;
  subtitle: string;
  slug: string;
  profileMedia: Media[];
  themeId: number | null;
};

export const toBlogHomeDraft = (home: BlogHome): BlogHomeDraft => ({
  title: home.title ?? "",
  subtitle: home.subtitle ?? "",
  slug: home.slug ?? "",
  profileMedia: home.profileMedia ?? [],
  themeId: home.contentPageThemeId ?? null,
});

export const BLOG_HOME_SLUG_MAX_LENGTH = 50;
export const BLOG_HOME_SLUG_EMPTY_ERROR = "Enter a URL slug";
export const BLOG_HOME_SLUG_CHARS_ERROR = "Use only letters, numbers, and hyphens";
export const BLOG_HOME_SLUG_LENGTH_ERROR = `Must be ${BLOG_HOME_SLUG_MAX_LENGTH} characters or fewer`;
export const BLOG_HOME_SLUG_TAKEN_ERROR = "This URL slug is already taken";

const BLOG_HOME_SLUG_PATTERN = /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/;

export const blogHomeSlugFormatError = (slug: string): string | null => {
  const value = slug.trim();
  if (!value) return BLOG_HOME_SLUG_EMPTY_ERROR;
  if (value.length > BLOG_HOME_SLUG_MAX_LENGTH) return BLOG_HOME_SLUG_LENGTH_ERROR;
  if (!BLOG_HOME_SLUG_PATTERN.test(value)) return BLOG_HOME_SLUG_CHARS_ERROR;
  return null;
};

export const isBlogHomeSlugTakenError = (error: unknown): boolean => {
  const message = typeof error === "string" ? error : error instanceof Error ? error.message : "";
  const lower = message.toLowerCase();
  return lower.includes("already been taken") || lower.includes("not unique") || lower.includes("duplicate entry");
};

export const profileMediaUri = (media: Media[] | undefined): string | undefined => media?.[0]?.uri;

export const blogHomePreviewUrl = (home: BlogHome, draft: BlogHomeDraft): string => {
  const slug = draft.slug.trim() || home.slug;
  if (!slug) return home.url || "";
  if (!home.url) return `/b/p/${slug}`;
  return home.url.replace(/\/[^/]+$/, `/${slug}`);
};

export const toBlogHomePreviewSummaries = (blogs: Blog[]) =>
  blogs.map(blog => ({
    guid: blog.guid,
    slug: blog.slug,
    title: blog.title || "Untitled",
    subtitle: blog.subtitle || undefined,
    excerpt: blog.excerpt || undefined,
    tags: blog.tags ?? [],
    readingTimeMinutes: blog.readingTimeMinutes ?? undefined,
    publishedAt: blog.publishedAt ?? undefined,
    url: blog.url,
    thumbnailUrl: blog.thumbnailUrl,
  }));

export const toBlogHomePreviewPayload = (
  home: BlogHome,
  draft: BlogHomeDraft,
  theme?: ContentPageTheme | null,
  blogs: Blog[] = []
) => {
  const title = draft.title.trim() || home.defaultTitle || "Your name";
  const subtitle = draft.subtitle.trim() || undefined;
  const profileImageUrl = profileMediaUri(draft.profileMedia) || home.defaultProfileImageUrl || undefined;

  return {
    guid: home.guid,
    url: blogHomePreviewUrl(home, draft),
    title,
    subtitle,
    profileImageUrl,
    author: {
      fullName: home.defaultTitle || title,
      photoUrl: home.defaultProfileImageUrl || undefined,
    },
    contentPageTheme: theme ?? home.effectiveContentPageTheme ?? home.contentPageTheme ?? undefined,
    blogs: toBlogHomePreviewSummaries(blogs),
  };
};
